import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { API_ENDPOINTS } from '@/config/api';
import { env } from '@/config/env';
import { currentConfig } from '@/features/hero';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { ModuleListResponse, ModuleSummary } from '../types';

// Stable module IDs for consistent mock data across requests
export const STABLE_MODULE_IDS = {
  ONGOING: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d',
  COMPLETED: 'f6e5d4c3-b2a1-4d5c-9e8f-7a6b5c4d3e2f',
} as const;

// Utility functions for generating consistent mock data
const generateAssessmentId = (): string => faker.string.uuid();

const generateAssessmentType = (): string =>
  faker.helpers.arrayElement([
    'Quiz',
    'Test',
    'Assignment',
    'Practice',
    'Mock Test',
  ]);

const generateAssessmentName = (): string =>
  faker.helpers.arrayElement([
    'Numbers and Mastering Addition',
    'Multiplication Maestro',
    'Super Subtraction and Integers',
    'Shapes and Measurements',
    'Factors, Multiples, LCM and HCF',
    'Math in Colors',
  ]);

const generateDifficultyLevel = (): string =>
  faker.helpers.arrayElement(['Easy', 'Medium', 'Hard']);

const generateTimeCategory = (): string =>
  faker.helpers.arrayElement(['TIMER', 'STOPWATCH']);

const generateAssessmentStatus = (): string =>
  faker.helpers.arrayElement([
    'Not Started',
    'In Progress',
    'Completed',
    'Overdue',
  ]);

const generateBannerType = (): string =>
  faker.helpers.arrayElement(['image', 'video', 'animation']);

// Generator for Assessment objects
const generateAssessment = () => ({
  assessmentId: generateAssessmentId(),
  assessmentType: generateAssessmentType(),
  assessmentName: generateAssessmentName(),
  startDate: faker.date.future().toISOString(),
  description: faker.lorem.sentences(2),
  duration: faker.number.int({ min: 30, max: 180 }),
  publishStatus: faker.datatype.boolean(),
  paperName: `Paper ${faker.number.int({ min: 1, max: 10 })}`,
  classID: faker.string.alphanumeric(8),
  completionStatus: false,
  assessmentCategoryType: faker.helpers.arrayElement(['MODULE', 'PRACTICE']),
  assessmentCategoryId: faker.string.uuid(),
  assessmentCategoryName: generateAssessmentName(),
  assessmentCategoryViewStatus: faker.helpers.arrayElement(['POST', 'PRE']),
  canGoback: faker.datatype.boolean(),
  timeCategory: generateTimeCategory(),
  reattemptAllowed: faker.datatype.boolean(),
  attemptStatus: faker.datatype.boolean(),
  solutionView: faker.datatype.boolean(),
  hintView: faker.datatype.boolean(),
  assessmentImageUrl: faker.image.url(),
  solutionSeen: faker.datatype.boolean(),
  lastAssessmentStatus: generateAssessmentStatus(),
  noOfAttempt: faker.number.int({ min: 0, max: 5 }),
  allowTakingAssessment: faker.datatype.boolean(),
  difficultyLevel: generateDifficultyLevel(),
  totalMarks: faker.number.int({ min: 50, max: 200 }),
  marksObtained: faker.number.int({ min: 0, max: 100 }),
  totalCoins: faker.number.int({ min: 10, max: 100 }),
  coinsEarned: faker.number.int({ min: 0, max: 100 }),
  isLocked: faker.datatype.boolean(),
  instruction: faker.lorem.paragraph(),
  displayName: faker.person.fullName(),
});

// Generator for ModuleSummary objects
const generateModuleSummary = (): ModuleSummary => ({
  totalAssessments: faker.number.int({ min: 5, max: 20 }),
  completedAssessments: faker.number.int({ min: 0, max: 15 }),
  percentile: faker.number.float({ min: 0, max: 100, fractionDigits: 2 }),
  moduleRanking: faker.number.int({ min: 1, max: 100 }),
  coinsEarned: faker.number.int({ min: 0, max: 500 }),
  accuracy: Array.from({ length: 5 }, () =>
    faker.number.float({ min: 0, max: 100, fractionDigits: 1 })
  ),
  proficiency: Array.from({ length: 5 }, () =>
    faker.number.float({ min: 0, max: 100, fractionDigits: 1 })
  ),
  speed: Array.from({ length: 5 }, () =>
    faker.number.float({ min: 0, max: 100, fractionDigits: 1 })
  ),
});

// Generator for Module objects
const generateModule = (status: string) => {
  const pendingAssessments = Array.from(
    { length: faker.number.int({ min: 1, max: 5 }) },
    generateAssessment
  );

  return {
    id: faker.string.uuid(),
    name: faker.helpers.arrayElement([
      'World of Finance',
      'Numbers and Mastering Addition',
      'Multiplication Maestro',
      'Numbers and Mastering Addition',
      'Super Subtraction and Integers',
      'Shapes and Measurements',
    ]),
    status,
    number: faker.number.int({ min: 1, max: 50 }),
    attendedSessionsCount: faker.number.int({ min: 0, max: 20 }),
    missedSessionsCount: faker.number.int({ min: 0, max: 5 }),
    banner: {
      type: generateBannerType(),
      url: faker.image.url(),
    },
    pendingTasks: {
      count: pendingAssessments.length,
      pendingAssessments,
    },
    summary: generateModuleSummary(),
    quizesCompletedQuestionsCount: faker.number.int({ min: 0, max: 100 }),
    quizAvailable: faker.datatype.boolean(),
  };
};

// Main mock data generator
export const generateModulesListMockData = (): ModuleListResponse => {
  const upComingModules = Array.from(
    { length: faker.number.int({ min: 2, max: 5 }) },
    () => ({
      ...generateModule('PAID_UPCOMING'),
      status: 'upcoming',
    })
  );

  const completedModules = Array.from(
    { length: faker.number.int({ min: 3, max: 8 }) },
    () => ({
      ...generateModule('PAID_COMPLETED'),
      status: 'completed',
    })
  );

  const onGoingModules = Array.from(
    { length: faker.number.int({ min: 1, max: 4 }) },
    () => ({
      ...generateModule('PAID_ONGOING'),
      status: 'ongoing',
    })
  );

  return {
    status: 'Success',
    message: 'Modules list retrieved successfully',
    responseBody: {
      upComingModules,
      completedModules,
      onGoingModules,
    },
  };
};

// Export mock data instance
export const mockModulesListData = generateModulesListMockData();

// Configurable mock handler that uses hero state config
export const modulesListMockHandler = http.get(
  `${env.API_URL}${API_ENDPOINTS.ASSESSMENTS.MODULES_LIST}`,
  async () => {
    await networkDelay();

    const config = currentConfig;

    // Always use configured assessments data to respect the mock configuration
    const configuredResponse: ModuleListResponse = {
      status: 'Success',
      message: 'Modules list retrieved successfully',
      responseBody: {
        upComingModules: [],
        completedModules: config.assessments.onGoingModules.map(module => ({
          attendedSessionsCount: faker.number.int({ min: 0, max: 20 }),
          missedSessionsCount: faker.number.int({ min: 0, max: 5 }),
          id: STABLE_MODULE_IDS.COMPLETED,
          name: module.name,
          status: 'PAID_COMPLETED',
          number: faker.number.int({ min: 1, max: 50 }),
          banner: {
            type: faker.helpers.arrayElement(['image', 'video', 'animation']),
            url: faker.image.url(),
          },
          pendingTasks: {
            count: module.pendingTasks.count,
            pendingAssessments: module.pendingTasks.pendingAssessments.map(
              assessment => ({
                marksObtained: faker.number.int({ min: 0, max: 100 }),
                assessmentId: assessment.assessmentId,
                assessmentType: generateAssessmentType(),
                assessmentName: generateAssessmentName(),
                startDate: faker.date.future().toISOString(),
                description: faker.lorem.sentences(2),
                duration: faker.number.int({ min: 30, max: 180 }),
                publishStatus: faker.datatype.boolean(),
                paperName: `Paper ${faker.number.int({ min: 1, max: 10 })}`,
                classID: faker.string.alphanumeric(8),
                completionStatus: assessment.completionStatus,
                assessmentCategoryType: faker.helpers.arrayElement([
                  'MODULE',
                  'PRACTICE',
                ]),
                assessmentCategoryId: faker.string.uuid(),
                assessmentCategoryName: generateAssessmentName(),
                assessmentCategoryViewStatus: faker.helpers.arrayElement([
                  'POST',
                  'PRE',
                ]),
                canGoback: faker.datatype.boolean(),
                timeCategory: generateTimeCategory(),
                reattemptAllowed: faker.datatype.boolean(),
                attemptStatus: faker.datatype.boolean(),
                solutionView: faker.datatype.boolean(),
                hintView: faker.datatype.boolean(),
                assessmentImageUrl: faker.image.url(),
                solutionSeen: faker.datatype.boolean(),
                lastAssessmentStatus: generateAssessmentStatus(),
                noOfAttempt: faker.number.int({ min: 0, max: 5 }),
                allowTakingAssessment: faker.datatype.boolean(),
                difficultyLevel: generateDifficultyLevel(),
                totalMarks: faker.number.int({ min: 50, max: 200 }),
                totalCoins: faker.number.int({ min: 10, max: 100 }),
                coinsEarned: faker.number.int({ min: 0, max: 100 }),
                isLocked: faker.datatype.boolean(),
                instruction: faker.lorem.paragraph(),
                displayName: faker.person.fullName(),
              })
            ),
          },
          summary: generateModuleSummary(),
          quizesCompletedQuestionsCount: faker.number.int({ min: 0, max: 100 }),
          quizAvailable: faker.datatype.boolean(),
        })),
        onGoingModules: config.assessments.onGoingModules.map(module => ({
          id: STABLE_MODULE_IDS.ONGOING,
          name: module.name,
          status: 'PAID_ONGOING',
          number: faker.number.int({ min: 1, max: 50 }),
          attendedSessionsCount: faker.number.int({ min: 0, max: 20 }),
          missedSessionsCount: faker.number.int({ min: 0, max: 5 }),
          banner: {
            type: faker.helpers.arrayElement(['image', 'video', 'animation']),
            url: faker.image.url(),
          },
          pendingTasks: {
            count: module.pendingTasks.count,
            pendingAssessments: module.pendingTasks.pendingAssessments.map(
              assessment => ({
                marksObtained: faker.number.int({ min: 0, max: 100 }),
                assessmentId: assessment.assessmentId,
                assessmentType: generateAssessmentType(),
                assessmentName: generateAssessmentName(),
                startDate: faker.date.future().toISOString(),
                description: faker.lorem.sentences(2),
                duration: faker.number.int({ min: 30, max: 180 }),
                publishStatus: faker.datatype.boolean(),
                paperName: `Paper ${faker.number.int({ min: 1, max: 10 })}`,
                classID: faker.string.alphanumeric(8),
                completionStatus: assessment.completionStatus,
                assessmentCategoryType: faker.helpers.arrayElement([
                  'MODULE',
                  'PRACTICE',
                ]),
                assessmentCategoryId: faker.string.uuid(),
                assessmentCategoryName: generateAssessmentName(),
                assessmentCategoryViewStatus: faker.helpers.arrayElement([
                  'POST',
                  'PRE',
                ]),
                canGoback: faker.datatype.boolean(),
                timeCategory: generateTimeCategory(),
                reattemptAllowed: faker.datatype.boolean(),
                attemptStatus: faker.datatype.boolean(),
                solutionView: faker.datatype.boolean(),
                hintView: faker.datatype.boolean(),
                assessmentImageUrl: faker.image.url(),
                solutionSeen: faker.datatype.boolean(),
                lastAssessmentStatus: generateAssessmentStatus(),
                noOfAttempt: faker.number.int({ min: 0, max: 5 }),
                allowTakingAssessment: faker.datatype.boolean(),
                difficultyLevel: generateDifficultyLevel(),
                totalMarks: faker.number.int({ min: 50, max: 200 }),
                totalCoins: faker.number.int({ min: 10, max: 100 }),
                coinsEarned: faker.number.int({ min: 0, max: 100 }),
                isLocked: faker.datatype.boolean(),
                instruction: faker.lorem.paragraph(),
                displayName: faker.person.fullName(),
              })
            ),
          },
          summary: generateModuleSummary(),
          quizesCompletedQuestionsCount: faker.number.int({ min: 0, max: 100 }),
          quizAvailable: faker.datatype.boolean(),
        })),
      },
    };

    return HttpResponse.json(configuredResponse);
  }
);

export const getModulesList = (): Promise<ModuleListResponse> => {
  return axios.get(API_ENDPOINTS.ASSESSMENTS.MODULES_LIST);
};
